import { useViewerSharpMotion, useViewerSharpness } from '../../viewer/sharpness';
import { useAccountChoice } from './accountChoice';

/**
 * [sharp, setSharp]: speed (false) or sharpness (true), which pixels the
 * viewer chooses a slide's level by (viewer/sharpness.ts). Signed in, the
 * choice is an account preference (`viewerSharpness`) that follows the
 * person between devices, as the viewer theme and glide do; the viewer
 * itself reads this browser's copy, kept in step here. Set from community's
 * Settings, enterprise's profile page and the viewer's bottom bar alike.
 */
export function useViewerSharpnessSetting(): [boolean, (sharp: boolean) => void] {
  const local = useViewerSharpness((s) => s.sharp);
  const setLocal = useViewerSharpness((s) => s.set);
  return useAccountChoice('viewerSharpness', local, setLocal);
}

/** What Settings says of Speed or Sharpness, in both editions. */
export const SHARPNESS_BLURB =
  'On high-density screens like Retina displays, Sharpness loads full-resolution image tiles once panning or zooming stops, providing sharper image details. This may affect loading times and performance depending on your device and connection speed.';

/**
 * What Settings says of Sharpness in Motion, in both editions. Shown only
 * under Sharpness, where it does something, and worded to be left off unless
 * it is needed (user, 2026-10-12): it is the costly one.
 */
export const SHARP_MOTION_BLURB =
  'Also loads full-resolution tiles while panning and zooming. Turn this on only if you need full detail while moving: it can noticeably slow the viewer depending on your device and connection speed.';

/**
 * [on, set]: Sharpness in Motion, sharpness's level while the view moves too
 * (viewer/sharpness.ts). An account preference (`viewerSharpMotion`) kept in
 * step with this browser's copy, as the choice above is; set from community's
 * Settings or enterprise's profile page, and read by the viewer's own pages
 * so the account's choice reaches it on any device.
 */
export function useViewerSharpMotionSetting(): [boolean, (on: boolean) => void] {
  const local = useViewerSharpMotion((s) => s.on);
  const setLocal = useViewerSharpMotion((s) => s.set);
  return useAccountChoice('viewerSharpMotion', local, setLocal);
}
